import React, { useState, useEffect, useContext, useRef } from 'react';
import HotelSearchPanel from './HotelSearchPanel';
import './Hotel.css';
import { LandingContext } from '../Profile/LandingContext'; // Import the context
import DefaultData from '../../CompanyData/DefaultData';


const useInView = (options) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setInView(true);
                observer.unobserve(entry.target);
            }
        }, options);

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            if (ref.current) {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                observer.unobserve(ref.current);
            }
        };
    }, [ref, options]);

    return [ref, inView];
};

const HotelSearch = () => {
    const landingData = useContext(LandingContext);
    const [currency, setCurrency] = useState(DefaultData.DEFAULTCURRENCY);
    const handleCurrencyChange = (newCurrency) => {
        setCurrency(newCurrency);
    };

    const [offersRef, offersInView] = useInView({ threshold: 0.1 });
    const [destinationsRef, destinationsInView] = useInView({ threshold: 0.1 });
    const [whyUsRef, whyUsInView] = useInView({ threshold: 0.1 });

    // Mock data remains the same...
    const hotelDeals = [
        { brand: 'Taj', logoUrl: '/assets/images/hotellogos/taj.png', title: 'City Break Specials', description: 'Discover top-rated city hotels with premium amenities.', discount: 'Up to 30% OFF', type: 'percent' },
        { brand: 'Marriott', logoUrl: '/assets/images/hotellogos/marriott.png', title: 'Luxury Resort Stays', description: 'Unwind at breathtaking resorts around the world.', discount: 'Flat ₹7500 OFF', type: 'flat' },
        { brand: 'Hyatt', logoUrl: '/assets/images/hotellogos/hyatt.png', title: 'Weekend Getaway Deals', description: 'Spontaneous stays at unbeatable prices.', discount: 'Starting @ ₹3,499', type: 'price' },
    ];
    const popularDestinations = [
        { name: 'Goa, India', image: 'https://images.unsplash.com/photo-1607577070449-531c2296916c?q=80&w=1025&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D' },
        { name: 'Dubai, UAE', image: 'https://images.unsplash.com/photo-1518684079-3c830dcef090?q=80&w=1974' },
        { name: 'Maldives', image: 'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8bWFsZGl2ZXN8ZW58MHx8MHx8fDA%3D' },
        { name: 'Bali, Indonesia', image: 'https://plus.unsplash.com/premium_photo-1682913629540-3857602b540c?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NXx8cmVzb3J0fGVufDB8fDB8fHww' },
    ];
    const whyChooseUs = [
        { icon: 'ri-price-tag-3-line', title: 'Best Price Guarantee', text: 'We offer the most competitive prices on 500,000+ hotels worldwide.' },
        { icon: 'ri-secure-payment-line', title: 'Secure Payments', text: 'Enjoy hassle-free and secure payment options for your booking.' },
        { icon: 'ri-customer-service-2-line', title: '24/7 Support', text: 'Our dedicated team is here to help you with your stay, around the clock.' },
    ];
    const heroStyle = {
        backgroundImage: `url("${landingData?.hotelBackgroundSrc || "/assets/images/Default_Hotel.jpg"}"), url("/assets/images/Default_Hotel.jpg")`,
    };


    return (
            <div className="main-content">
                 <section className="hotel-hero-section" style={heroStyle}>
                    <h1 className="hotel-hero-title">Search and Book hotels.</h1>
                    <p className="hotel-hero-subtitle">Find the best hotel deals for your perfect stay</p>
                    <div className="hotel-search-container">
                        <HotelSearchPanel />
                    </div>
                </section>
               <div className="page-content" style={{ paddingTop: 0 }}>
                    <div className="container">
                        <section ref={offersRef} className={`py-5 section-container ${offersInView ? 'is-visible' : ''}`}>
                            <h2 className="section-title">Exclusive Hotel Offers</h2>
                            <div className="row g-4">
                                {hotelDeals.map((deal, index) => (
                                    <div className="col-lg-4 col-md-6" key={index}>
                                        <div className={`offer-card offer-card-${deal.type}`}>
                                            <div className="offer-card-header">
                                                <img src={deal.logoUrl} alt={`${deal.brand} logo`} className="airline-logo" />
                                                <span className="discount-badge">{deal.discount}</span>
                                            </div>
                                            <div className="offer-card-body">
                                                <h5 className="card-title">{deal.title}</h5>
                                                <p className="card-text">{deal.description}</p>
                                                <a href="#!" className="stretched-link">Book Now <i className="ri-arrow-right-line"></i></a>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                        <section ref={destinationsRef} className={`py-5 section-container ${destinationsInView ? 'is-visible' : ''}`}>
                            <h2 className="section-title">Explore Popular Stays</h2>
                            <div className="row g-4">
                                {popularDestinations.map((dest, index) => (
                                    <div className="col-lg-3 col-md-6" key={index}>
                                        <a href="#!" className="destination-card-modern">
                                            <img src={dest.image} alt={dest.name} className="card-img" />
                                            <div className="card-img-overlay">
                                                <h4 className="card-title">{dest.name}</h4>
                                            </div>
                                        </a>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section ref={whyUsRef} className={`py-5 section-container why-us-section ${whyUsInView ? 'is-visible' : ''}`}>
                             <h2 className="section-title">Why Book With Us?</h2>
                             <div className="row g-4">
                                {whyChooseUs.map((item, index) => (
                                <div className="col-lg-4 col-md-6" key={index}>
                                    <div className="why-us-item">
                                        <div className="why-us-icon">
                                            <i className={item.icon}></i>
                                        </div>
                                        <div className="why-us-content">
                                            <h5 className="title">{item.title}</h5>
                                            <p className="text">{item.text}</p>
                                        </div>
                                    </div>
                                </div>
                                ))}
                             </div>
                        </section>
                    </div>
                </div>
            </div>
    );
};

export default HotelSearch; 